import { DocusealForm } from "@docuseal/vue";
import {
  PREVIEW_TEMPLATE_URL,
  DOCUSEAL_SCRIPT_URL,
} from "@/utils/constants/constants.js";
import axios from "axios";
import $ from "jquery";

export default {
  components: {
    DocusealForm,
  },
  data: () => ({
    docuseal_slug: null,
    docuseal_loaded: false,
    docuseal_form_instance: null,
    is_loading_document: false,
    document_loaded: false,
    preview_loading: false,
    preview_error: null,
    embed_src: null,
    form_data: {},
  }),
  methods: {
    async load_template_preview(signing = false) {
      this.preview_loading = true;
      this.preview_error = null;
      this.docuseal_slug = null;
      this.embed_src = null;
      this.is_loading_document = true;
      this.document_loaded = false;

      try {
        if (!this.template_id) {
          this.show_error("No template ID found");
          return;
        }

        await this.fetch_contract_status();

        const form_submission_id = this.$route.query.application_id;
        const primary_signer = this.signers?.[0] || null;

        const payload = this.build_docuseal_payload(
          form_submission_id,
          signing,
          primary_signer
        );

        if (!window.docusealForm) {
          await this.load_docuseal_script();
        }

        const response = await axios.post(PREVIEW_TEMPLATE_URL, payload, {
          headers: this.headers,
          params: {
            template_id: Number(this.template_id) || this.template_id,
            project_id: this.project_id,
          },
          timeout: 30000,
        });
        this.process_docuseal_response(response.data);
        await this.setup_docuseal_form();

        if (!signing) {
          this.add_timeline_event({
            event_type: "viewed",
            description: "Document previewed",
            timestamp: new Date().toISOString(),
            user: this.founder_name,
          });
        }
      } catch (error) {
        this.handle_template_error(error, signing);
      } finally {
        this.preview_loading = false;
        this.is_loading_document = false;
        setTimeout(() => {
          this.document_loaded = true;
        }, 300);
      }
    },

    build_docuseal_payload(form_submission_id, signing, primary_signer) {
      const payload = {
        application_id: Number(form_submission_id) || null,
        form_id: Number(this.received_form_id) || null,
        send_email: signing,
        form_submission_id: form_submission_id ? Number(form_submission_id) : null,
        mode: signing ? "sign" : "preview",
        user_id: this.received_user_id || this.user_id,
        profile_role_id: this.received_profile_role_id || this.profile_role_id,
        project_id: this.project_id,
      };

      if (signing && primary_signer) {
        if (primary_signer.email) payload.email = primary_signer.email;
        if (primary_signer.name || primary_signer.surname) {
          payload.name = `${primary_signer.name || ""} ${primary_signer.surname || ""
            }`.trim();
        }
        if (primary_signer.role) payload.role = primary_signer.role;
      }

      return payload;
    },

    handle_template_error(error, signing) {
      this.preview_error = this.get_error_message(error);
      this.show_error(this.preview_error);

      this.add_timeline_event({
        event_type: "error",
        description: `Failed to load document: ${this.preview_error}`,
        timestamp: new Date().toISOString(),
        user: "System",
      });

      if (signing) {
        this.decline_signer("Failed to load signing form");
      }
    },

    get_error_message(error) {
      if (error.response) {
        return (
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText
        );
      }
      return (
        this.show_error()
      );
    },

    process_docuseal_response(data) {
      if (!data) {
        this.show_error("No data received from server");
        return;
      }

      if (!data.slug) {
        this.show_error("Missing document identifier in response");
        return;
      }

      let submission_id = data.submission_id;
      if (Array.isArray(submission_id)) {
        submission_id = submission_id[0];
      } else if (
        typeof submission_id !== "string" &&
        typeof submission_id !== "number"
      ) {
        submission_id = data.id || null;
      }

      if (typeof submission_id === "number" && !isNaN(submission_id)) {
        this.submission_ids.push(submission_id);
        this.current_submission_id = submission_id;
      } else {
        this.current_submission_id = null;
      }

      this.docuseal_slug = data.slug;
      this.embed_src = data.embed_src || null;
      this.form_data = data.tags || data.form_data || {};
    },

    on_form_complete() {
      this.show_success("Document signed successfully!");
      this.fetch_contract_status();
      this.add_timeline_event({
        event_type: "signature",
        description: `Document signed by ${this.founder_name}`,
        timestamp: new Date().toISOString(),
        user: this.founder_name,
      });
    },

    async setup_docuseal_form(retries = 30) {
      if (!this.docuseal_slug) return;

      const container = $("#docuseal-viewer")[0];
      if (!container) {
        if (retries > 0) {
          setTimeout(() => this.setup_docuseal_form(retries - 1), 500);
        } else {
          this.show_error("DocuSeal container not found");
        }
        return;
      }

      container.innerHTML = "";
      const form = document.createElement("docuseal-form");
      form.setAttribute("id", "docusealForm");
      this.configure_docuseal_form(form);
      container.appendChild(form);
    },

    configure_docuseal_form(form) {
      const src_url =
        this.embed_src || `https://docuseal.co/d/${this.docuseal_slug}`;
      form.setAttribute("data-src", src_url);

      const primary_signer = this.signers?.[0] || null;
      const signer_email = primary_signer?.email;
      const signer_name = primary_signer?.name;
      const signer_surname = primary_signer?.surname;
      const signer_role = primary_signer?.role;

      form.setAttribute("data-zoom-enabled", "true");
      form.setAttribute("data-autoscroll-fields", "true");
      form.setAttribute("data-with-field-names", "true");
      form.setAttribute("data-order-as-on-page", "true");

      if (this.is_signing_mode) {
        this.configure_signing_mode(
          form,
          signer_email,
          signer_name,
          signer_surname,
          signer_role
        );
      } else {
        this.configure_preview_mode(form);
      }

      if (Object.keys(this.form_data).length > 0) {
        form.setAttribute("data-values", JSON.stringify(this.form_data));
      }

      if (this.current_submission_id) {
        form.setAttribute(
          "data-external-id",
          this.current_submission_id.toString()
        );
      }
      form.addEventListener("completed", (e) =>
        this.on_form_complete(e.detail)
      );
      form.addEventListener("decline", (e) => this.handle_decline(e));
      form.addEventListener("loaded", () => this.on_docuseal_loaded());
    },

    configure_signing_mode(form, email, name, surname, role) {
      form.setAttribute("data-preview", "false");
      form.setAttribute("data-readonly", "false");

      if (email) form.setAttribute("data-email", email);
      if (name || surname) {
        form.setAttribute("data-name", `${name || ""} ${surname || ""}`.trim());
      }
      if (role) form.setAttribute("data-role", role);

      form.setAttribute("data-allow-typed-signature", "true");
      form.setAttribute("data-reuse-signature", "true");
      form.setAttribute("data-remember-signature", "true");
      form.setAttribute("data-with-decline", "true");
      form.setAttribute("data-with-complete-button", "true");
      form.setAttribute("data-allow-to-resubmit", "false");
      form.setAttribute("data-send-copy-email", "true");
      form.setAttribute("data-go-to-last", "true");
      form.setAttribute("data-skip-fields", "false");
      form.setAttribute("data-with-title", "true");
      form.setAttribute("data-with-download-button", "true");
      form.setAttribute("data-with-send-copy-button", "true");
      form.setAttribute("data-expand", "true");
      form.setAttribute("data-minimize", "false");

      if (this.completed_redirect_url) {
        form.setAttribute(
          "data-completed-redirect-url",
          this.completed_redirect_url
        );
      }

      form.setAttribute(
        "data-completed-message",
        JSON.stringify({
          title: "Signing Complete!",
          body: "Thank you for signing the document.",
        })
      );
    },

    configure_preview_mode(form) {
      form.setAttribute("data-preview", "true");
      form.setAttribute("data-readonly", "true");
      form.setAttribute("data-tags-display", "fixed");
      form.setAttribute("data-tags-toggle-enabled", "true");
      form.setAttribute("data-with-download-button", "false");
      form.setAttribute("data-with-send-copy-button", "false");
      form.setAttribute("data-with-complete-button", "false");
    },

    handle_decline(e) {
      this.show_warning("Document signing was declined", e);
      this.decline_signer("User declined through DocuSeal interface", e);
    },

    async load_docuseal_script() {
      return new Promise((resolve, reject) => {
        if (window.docusealForm) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = DOCUSEAL_SCRIPT_URL;
        script.onload = () => {
          this.docuseal_loaded = true;
          resolve();
        };
        script.onerror = () => {
          this.show_error("Failed to load DocuSeal script");
          reject();
        };
        document.body.appendChild(script);
      });
    },

    on_docuseal_loaded() {
      this.is_loading_document = false;
      this.document_loaded = true;
    },
  },
};